/**
 * AdminIdleLockGuard — wraps the Admin shell with an idle-session timeout.
 *
 * Mounted ONCE in <Admin /> after authentication so it can:
 *   1. Read the Owner-configured timeout from the backend (5/10/15 min).
 *   2. Track activity (mousemove/keydown/click/scroll/touchstart).
 *   3. Show a warning modal at T-60s with a "Stay logged in" button.
 *   4. On timeout: POST an `admin_idle_timeout` event to the audit log,
 *      call the parent `onTimeout()` (which clears tokens + redirects to
 *      /admin/login with ?reason=idle_locked), all best-effort.
 *
 * Scope: only mounted under /admin routes — public + member pages are
 * unaffected. The hook deactivates automatically when enabled=false.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useIdleTimer } from "../../hooks/useIdleTimer";
import {
  adminIdleConfigGet,
  adminIdleTimeoutEvent,
} from "../../lib/api";

const WARNING_MS = 60_000;
const DEFAULT_TIMEOUT_MIN = 10;

export const AdminIdleLockGuard = ({ opToken, userToken, onTimeout, children }) => {
  const loc = useLocation();
  const [timeoutMin, setTimeoutMin] = useState(DEFAULT_TIMEOUT_MIN);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const firedOnce = useRef(false);
  const tickInterval = useRef(null);

  // Hydrate the configured timeout (any admin can read it).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await adminIdleConfigGet(opToken || null, userToken || null);
        if (!cancelled && cfg?.timeout_minutes) setTimeoutMin(cfg.timeout_minutes);
      } catch { /* silent — keep default 10min */ }
    })();
    return () => { cancelled = true; };
  }, [opToken, userToken]);

  const stopCountdown = useCallback(() => {
    if (tickInterval.current) { clearInterval(tickInterval.current); tickInterval.current = null; }
  }, []);

  const handleTimeout = useCallback(async () => {
    if (firedOnce.current) return;
    firedOnce.current = true;
    stopCountdown();
    setWarningOpen(false);
    // Best-effort: log the lock event server-side BEFORE we clear creds.
    try {
      await adminIdleTimeoutEvent(opToken || null, userToken || null, {
        path: loc.pathname,
        timeout_minutes: timeoutMin,
      });
    } catch { /* silent — token may already be invalid */ }
    if (onTimeout) onTimeout();
  }, [opToken, userToken, loc.pathname, timeoutMin, onTimeout, stopCountdown]);

  const handleWarning = useCallback(() => {
    setWarningOpen(true);
    setSecondsLeft(Math.round(WARNING_MS / 1000));
    stopCountdown();
    tickInterval.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) return 0;
        return s - 1;
      });
    }, 1000);
  }, [stopCountdown]);

  const { reset } = useIdleTimer({
    timeoutMs: timeoutMin * 60_000,
    warningMs: WARNING_MS,
    enabled: true,
    onWarning: handleWarning,
    onTimeout: handleTimeout,
  });

  const stayLoggedIn = useCallback(() => {
    setWarningOpen(false);
    stopCountdown();
    reset();
  }, [reset, stopCountdown]);

  useEffect(() => () => stopCountdown(), [stopCountdown]);

  return (
    <>
      {children}
      {warningOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          role="alertdialog"
          aria-labelledby="idle-warning-title"
          aria-describedby="idle-warning-desc"
          data-testid="admin-idle-warning"
        >
          <div className="w-[min(420px,92vw)] border border-[#D92D20] bg-[#0a0907] p-6 shadow-2xl">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert size={18} className="text-[#D92D20]" />
              <h2
                id="idle-warning-title"
                className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D92D20]"
              >
                Idle Lock Imminent
              </h2>
            </div>
            <p
              id="idle-warning-desc"
              className="text-[14px] text-[#E8E1D2] leading-relaxed mb-1"
            >
              Admin session will lock in{" "}
              <span
                className="font-mono text-[#D92D20]"
                data-testid="admin-idle-countdown"
              >
                {secondsLeft}
              </span>{" "}
              seconds due to inactivity.
            </p>
            <p className="text-[12px] text-[#A39E93] font-mono mb-5">
              Press the button below to stay signed in.
            </p>
            <button
              type="button"
              onClick={stayLoggedIn}
              data-testid="admin-idle-stay"
              className="w-full px-4 py-2 border border-[#D92D20] text-[#D92D20] font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#D92D20] hover:text-[#0a0907] transition-colors"
            >
              Stay logged in
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminIdleLockGuard;
